import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CCard,
  CCardBody,
  CForm,
  CFormInput,
  CButton,
  CCol,
  CSpinner,
  CModal,
  CModalBody,
  CProgress,
  CBadge
} from '@coreui/react';
import { FirebaseContext, withFirebase } from '../../firebase';
import { AuthUserContext } from '../../session/context';
import { useLocalStorage } from '../../../utils/useLocalStorage';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../../assets/AA-01.jpg';
import { getAuth, createUserWithEmailAndPassword, sendEmailVerification, signInWithEmailAndPassword, EmailAuthProvider } from 'firebase/auth';
import { getDatabase, ref, get, set } from 'firebase/database';
import config from '../../../utils/EnvironmentSwitcher';
import { seedDemoObservationsForUser } from '../../../utils/useDemoDataSeeding';
const auth = getAuth();
const database = getDatabase();

// Separate Login Form Component
const LoginForm = ({ onSwitchToSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const firebase = useContext(FirebaseContext);
  const { authUser, setAuthUser } = useContext(AuthUserContext);
  const [fhirToken, setFhirToken] = useState(null);

  // Loading state for login progress
  const [loginProgress, setLoginProgress] = useState({
    isLoading: false,
    currentStep: '',
    progress: 0,
    steps: []
  });

  // Progress helper functions
  const startLoginProgress = () => {
    const steps = [
      'Authenticating credentials',
      'Verifying account',
      'Setting up session',
      'Connecting to services',
      'Preparing dashboard'
    ];
    setLoginProgress({
      isLoading: true,
      currentStep: steps[0],
      progress: 0,
      steps: steps
    });
  };

  const updateLoginStep = (stepIndex) => {
    setLoginProgress(prev => ({
      ...prev,
      currentStep: prev.steps[stepIndex],
      progress: ((stepIndex + 1) / prev.steps.length) * 100
    }));
  };

  const completeLogin = () => {
    setLoginProgress(prev => ({
      ...prev,
      currentStep: 'Login complete! Redirecting...',
      progress: 100
    }));
    setTimeout(() => {
      setLoginProgress({ isLoading: false, currentStep: '', progress: 0, steps: [] });
    }, 1000);
  };

  const failLogin = (errorMessage) => {
    setLoginProgress(prev => ({
      ...prev,
      currentStep: `Login failed: ${errorMessage}`,
      progress: 0
    }));
    setTimeout(() => {
      setLoginProgress({ isLoading: false, currentStep: '', progress: 0, steps: [] });
    }, 2000);
  };

  const syncOnboardingFromAgents = async (idToken, uid) => {
    try {
      if (!idToken || !uid) return;
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (!res.ok) return;
      const data = await res.json();
      const count = Array.isArray(data)
        ? data.length
        : Array.isArray(data?.agents)
          ? data.agents.length
          : Array.isArray(data?.items)
            ? data.items.length
            : typeof data?.count === 'number'
              ? data.count
              : 0;
      if (count > 0) {
        localStorage.setItem(`mie_has_agents_${uid}`, 'true');
        await set(ref(database, `users/${uid}/onboardingComplete`), true);
      }
    } catch (err) {
      console.warn('LocalLogin: failed to sync onboarding from agents', err);
    }
  };

  const registerUserBestEffort = async (idToken, body = {}) => {
    try {
      const response = await fetch(`${config.apiUrl}/api/registerUser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      });
      if (response.ok) return { ok: true };
      const text = await response.text().catch(() => '');
      let parsed = null;
      try { parsed = text ? JSON.parse(text) : null; } catch (_) { parsed = null; }
      return {
        ok: false,
        status: response.status,
        message: parsed?.message || parsed?.error || text || `registerUser failed (${response.status})`,
      };
    } catch (err) {
      return { ok: false, status: 0, message: err?.message || 'registerUser network error' };
    }
  };

  const exchangeFhirTokenViaApi = async (idToken) => {
    const response = await fetch(`${config.apiUrl}/api/fhir/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ firebaseToken: idToken }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `FHIR token exchange failed (${response.status})`);
    }
    const data = await response.json();
    const token = data?.fhirToken;
    if (!token) throw new Error('FHIR token exchange returned empty token');
    return token;
  };

  const handleNext = (event) => {
    event.preventDefault();
    if (email) {
      setShowPassword(true);
    }
  };

  const handleBack = () => {
    setShowPassword(false);
    setPassword(''); // Clear password when going back
  };

  const handleEmailLogin = async () => {
    startLoginProgress();

    try {
      // Step 1: Authenticate credentials
      const provider = new EmailAuthProvider();
      const result = await signInWithEmailAndPassword(auth, email, password);
      setPassword('');
      const user = result.user;
      const token = await user.getIdToken();

      updateLoginStep(1); // Verifying account

      // Store user data
      const emailUserData = {
        miePhoto: user.photoURL || 'defaultPhotoURL',
        mieToken: token,
        mieDisplayName: user.displayName || user.email,
        mieUid: user.uid,
        mieWalletExists: false,
        mieEmail: user.email,
        verified: user.emailVerified,
      };

      // Check if user exists in Firebase Database
      const userRef = ref(database, `users/${user.uid}`);
      const snapshot = await get(userRef);

      updateLoginStep(2); // Setting up session

      if (snapshot.exists()) {
        const firebaseUserData = snapshot.val();
        const userSessionData = {
          ...emailUserData,
          ...firebaseUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      } else {
        // Create new user in Firebase
        const newUserData = {
          email: user.email,
          name: user.displayName || user.email.split('@')[0],
          phone: "TBD",
          address: "TBD",
          paymentID: "0",
          userID: user.uid,
          userType: "TRD",
          mieWalletExists: false,
          mieUserID: user.uid,
          verified: user.emailVerified,
        };

        await set(userRef, newUserData);
        const userSessionData = {
          ...emailUserData,
          ...newUserData,
        };
        localStorage.setItem('userSession', JSON.stringify(userSessionData));
      }

      updateLoginStep(3); // Connecting to services

      const registerResult = await registerUserBestEffort(token);
      if (registerResult.ok) {
        console.log('User registered successfully');
      } else {
        console.warn('LocalLogin: registerUser failed (continuing):', registerResult.message);
      }

      const fhirAccessToken = await exchangeFhirTokenViaApi(token);
      setFhirToken(fhirAccessToken);
      localStorage.setItem('mieFToken', fhirAccessToken);

      updateLoginStep(4); // Preparing dashboard

      // Seed demo observation data for new users (runs in background)
      // This populates the health dashboard with sample vital signs
      seedDemoObservationsForUser({
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        stsTokenManager: { accessToken: token }
      }).then(result => {
        if (result.success) {
          console.log(`Demo data seeded: ${result.createdCount} observations created`);
        } else if (result.skipped) {
          console.log('Demo data seeding skipped:', result.reason);
        }
      }).catch(err => {
        console.warn('Demo data seeding failed (non-blocking):', err.message);
      });

      completeLogin();
      toast.success("Login successful!", { duration: 1000 });

      // Update auth context and navigate
      setAuthUser(user);
      await syncOnboardingFromAgents(token, user.uid);
      const unsubscribe = auth.onAuthStateChanged((authUser) => {
        if (authUser) {
          navigate('/dashboard');
          unsubscribe();
        }
      });
    } catch (error) {
      console.error("Error during email login:", error);

      // Handle specific Firebase auth errors
      const errorMessage = error.code === '400' || error?.error?.message === 'INVALID_LOGIN_CREDENTIALS'
        ? 'Invalid email or password'
        : error.message || 'An error occurred during login';

      failLogin(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Clear password field on error
      setPassword('');
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    await handleEmailLogin();
  };

  return (
    <>
      <h1 className="welcome-text">Welcome back</h1>

      <CForm onSubmit={showPassword ? handleLogin : handleNext}>
        <div className="form-field">
          <label className="input-label">
            Email address or phone number*
          </label>
          <CFormInput
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-input"
            placeholder="Enter your email"
            disabled={showPassword || loginProgress.isLoading}
            readOnly={showPassword}
          />
        </div>

        {showPassword && (
          <div className="form-field">
            <label className="input-label">
              Password*
            </label>
            <CFormInput
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="login-input"
              placeholder="Enter your password"
              autoFocus
              disabled={loginProgress.isLoading}
            />
          </div>
        )}

        <CCol xs={12}>
          <div className="button-container">
            <CButton
              align="center"
              color="primary"
              className="gradient-button mt-2 text-center"
              type="submit"
              disabled={loginProgress.isLoading}
            >
              {loginProgress.isLoading ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Signing in...
                </>
              ) : showPassword ? 'Login' : 'Next'}
            </CButton>
          </div>

          {!showPassword && !loginProgress.isLoading && (
            <div className="signup-prompt">
              Don't have an account?{' '}
              <button
                onClick={onSwitchToSignUp}
                className="signup-link"
                type="button"
              >
                Sign Up
              </button>
            </div>
          )}

          {showPassword && !loginProgress.isLoading && (
            <div className="signup-prompt">
              <button
                onClick={handleBack}
                className="back-button"
                type="button"
              >
                Back
              </button>
            </div>
          )}
        </CCol>
      </CForm>

      {/* Login Progress Modal */}
      {loginProgress.isLoading && (
        <CModal
          visible={true}
          backdrop="static"
          keyboard={false}
          alignment="center"
          size="sm"
        >
          <CModalBody className="text-center p-4">
            <div className="login-progress">
              <div className="mb-3">
                <CSpinner
                  color="primary"
                  style={{ width: '3rem', height: '3rem' }}
                />
              </div>

              <h5 className="mb-3">Signing you in...</h5>

              <div className="progress-info mb-3">
                <small className="mb-2 d-block mb-2">
                  {loginProgress.currentStep}
                </small>

                <CProgress
                  value={loginProgress.progress}
                  className="mb-2"
                  style={{ height: '8px' }}
                />

                <small className="mb-2">
                  {Math.round(loginProgress.progress)}% Complete
                </small>
              </div>

              <div className="login-steps">
                {loginProgress.steps.map((step, index) => {
                  const isCompleted = index < (loginProgress.progress / 100) * loginProgress.steps.length;
                  const isCurrent = Math.floor((loginProgress.progress / 100) * loginProgress.steps.length) === index;

                  return (
                    <div
                      key={index}
                      className={`step-item d-flex align-items-center mb-2 ${isCompleted ? 'text-success' : isCurrent ? 'text-primary' : 'mb-2'
                        }`}
                      style={{ fontSize: '0.85rem' }}
                    >
                      <div className="step-indicator me-2">
                        {isCompleted ? (
                          <CBadge color="success" shape="rounded-pill">✓</CBadge>
                        ) : isCurrent ? (
                          <CSpinner size="sm" color="primary" />
                        ) : (
                          <CBadge color="light" shape="rounded-pill">{index + 1}</CBadge>
                        )}
                      </div>
                      <span className={isCompleted ? 'text-decoration-line-through' : ''}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-3">
                <small className="mb-2">
                  This usually takes just a few seconds
                </small>
              </div>
            </div>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

// Add new ConfirmationScreen component
const ConfirmationScreen = ({ email, onBackToLogin }) => {
  return (
    <>
      <h1 className="welcome-text">Check your inbox</h1>

      <div className="verification-text">
        We sent a verification email to<br />
        <span className="email-highlight">{email}</span>
      </div>

      <CCol xs={12}>
        <div className="button-container">
          <CButton
            color="primary"
            className="gradient-button mt-2"
            onClick={onBackToLogin}
          >
            Back to Login
          </CButton>
        </div>
      </CCol>
    </>
  );
};

// Modify SignUpForm to show confirmation screen
const SignUpForm = ({ onSwitchToLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useLocalStorage('userSession', null);

  const handleSignUp = async (event) => {
    event.preventDefault();
    setIsCreating(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send verification email
      await sendEmailVerification(user);

      // Show confirmation screen
      setShowConfirmation(true);

      const authUser = {
        miePhoto: 'defaultPhotoURL',
        mieToken: await user.getIdToken(),
        mieDisplayName: user.displayName || email,
        mieUid: user.uid,
        mieEmail: user.email,
        userType: 'regular',
      };

      localStorage.setItem('userSession', JSON.stringify(authUser));
      setUserData(authUser);

      toast.success("Verification email sent!");
    } catch (err) {
      console.error('Signup error:', err);
      toast.dismiss();
      toast.error(err.message || 'An error occurred during signup');
    } finally {
      setIsCreating(false);
    }
  };

  if (showConfirmation) {
    return <ConfirmationScreen
      email={email}
      onBackToLogin={onSwitchToLogin}
    />;
  }

  return (
    <>
      <h1 className="welcome-text">Create Account</h1>

      <CForm onSubmit={handleSignUp}>
        <div className="form-field">
          <label className="input-label">
            Email address*
          </label>
          <CFormInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="login-input"
            placeholder="Enter your email"
            required
            disabled={isCreating}
          />
        </div>

        <div className="form-field">
          <label className="input-label">
            Password*
          </label>
          <CFormInput
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
            placeholder="Create a password"
            required
            disabled={isCreating}
          />
        </div>

        <CCol xs={12}>
          <div className="button-container">
            <CButton
              align="center"
              color="primary"
              className="gradient-button mt-2 text-center"
              type="submit"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <CSpinner size="sm" className="me-2" />
                  Creating account...
                </>
              ) : 'Create'}
            </CButton>
          </div>

          {!isCreating && (
            <div className="signup-prompt">
              Already have an account?{' '}
              <button
                onClick={onSwitchToLogin}
                className="signup-link"
                type="button"
              >
                Sign In
              </button>
            </div>
          )}
        </CCol>
      </CForm>
    </>
  );
};

// Main Component
const LocalLogin = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  // Preload logo image
  React.useEffect(() => {
    const img = new Image();
    img.onload = () => setPageReady(true);
    img.onerror = () => setPageReady(true); // Show page even on error
    img.src = logo;

    // Timeout fallback
    const timeout = setTimeout(() => setPageReady(true), 2000);
    return () => clearTimeout(timeout);
  }, []);

  // Show loading spinner while page initializes
  if (!pageReady) {
    return (
      <div className="login-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <CSpinner
            color="primary"
            style={{ width: '3rem', height: '3rem' }}
          />
          <p className="mt-3 mb-2">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrapper">
      <CCard className="login-card">
        {/* 
          Layout notes:
          - _local-login.scss positions .image-container absolutely, but its left/transform rules can drift.
          - We override those values inline to keep the logo centered across breakpoints.
          - We also override the card-body flex justification so footer text never overlaps the CTA button.
        */}
        <CCardBody
          className="local-login-card-body"
          style={{
            // Override SCSS defaults for predictable vertical spacing.
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            // Reserve space for the overlapping logo.
            paddingTop: '72px',
          }}
        >
          <div
            className="image-container"
            style={{
              position: 'absolute', // Ensure this is explicit
              left: '70%',
              transform: 'translateX(-50%)',
              top: '10px',
              zIndex: 10, // Keeps it above the card borders
            }}
          >
            <img
              src={logo}
              alt="mIexistence"
              className="img-circle-login"
              style={{
                width: '100px',
                height: '100px',
                objectFit: 'cover',
                borderRadius: '50%', // Move this here if CSS is being overridden
                display: 'block'
              }}
            />
          </div>

          <div
            className="login-content"
            style={{
              // Center the form block, while keeping it full-width.
              width: '100%',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {isSignUp ? (
              <SignUpForm onSwitchToLogin={() => setIsSignUp(false)} />
            ) : (
              <LoginForm onSwitchToSignUp={() => setIsSignUp(true)} />
            )}
          </div>

          <p
            align="center"
            className="demo-text"
            style={{
              marginTop: '16px',
              marginBottom: 0,
            }}
          >
            This is MiEROS demo - do not store personal data
          </p>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default withFirebase(LocalLogin);


